(() => {
  const PATHS = {
    kickstarter: {
      label: "Kickstarter backer answers",
      note: "A pledge supports a project. It is not a store order.",
      contextTitle: "Kickstarter",
      contextCopy: "Campaign rewards, pledge payments, delivery estimates, and backer updates follow Kickstarter\u2019s campaign flow.",
      resultsKicker: "For Kickstarter backers",
      resultsHeading: "Questions before you pledge.",
      searchPlaceholder: "Search pledges, rewards, shipping\u2026",
      emptySwitch: "Check direct pre-order",
      topics: ["All", "Launch", "Pledge", "Rewards", "Shipping", "Support"],
      facts: [
        ["Commitment", "A Kickstarter pledge"],
        ["Payment", "Collected if the campaign funds"],
        ["Terms", "Campaign and platform terms"]
      ]
    },
    direct: {
      label: "Direct pre-order answers",
      note: "Direct pre-orders are a later route and are not open yet.",
      contextTitle: "Direct order",
      contextCopy: "When direct pre-orders open, the 8ight checkout will show the package, price, payment timing, delivery estimate, and customer terms before confirmation.",
      resultsKicker: "For direct customers",
      resultsHeading: "Questions before you order.",
      searchPlaceholder: "Search payment, delivery, returns\u2026",
      emptySwitch: "Check Kickstarter answers",
      topics: ["All", "Ordering", "Payment", "Product", "Shipping", "Returns", "App"],
      facts: [
        ["Commitment", "An order through 8ight"],
        ["Payment", "Shown before confirmation"],
        ["Terms", "8ight pre-order terms"]
      ]
    }
  };

  const normalize = (value) => value
    .toLowerCase()
    .replace(/[\u2019']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  function init(root) {
    if (!root || root.dataset.preorderFaqReady === "true") return;
    root.dataset.preorderFaqReady = "true";

    const pathInputs = [...root.querySelectorAll("[data-audience-input]")];
    const items = [...root.querySelectorAll("[data-faq-item]")];
    const topicRail = root.querySelector("[data-topic-rail]");
    const faqList = root.querySelector("[data-faq-list]");
    const search = root.querySelector("[data-search]");
    const clearSearch = root.querySelector("[data-clear-search]");
    const emptyState = root.querySelector("[data-empty-state]");
    const resultCount = root.querySelector("[data-result-count]");
    const liveStatus = root.querySelector("[data-live-status]");
    const audienceLabel = root.querySelector("[data-audience-label]");
    const audienceNote = root.querySelector("[data-audience-note]");
    const contextTitle = root.querySelector("[data-context-title]");
    const contextCopy = root.querySelector("[data-context-copy]");
    const contextFacts = root.querySelector("[data-context-facts]");
    const resultsKicker = root.querySelector("[data-results-kicker]");
    const resultsHeading = root.querySelector("[data-results-heading]");
    const emptySwitch = root.querySelector("[data-empty-switch]");
    const pathSwitcher = root.querySelector("[data-path-switcher]");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const requestedAudience = new URL(window.location.href).searchParams.get("audience");
    const state = {
      audience: requestedAudience === "direct" ? "direct" : "kickstarter",
      topic: "All",
      query: ""
    };
    let announceTimer = 0;

    function activePath() {
      return PATHS[state.audience];
    }

    function itemMatches(item) {
      if (item.dataset.audience !== state.audience) return false;
      if (state.topic !== "All" && item.dataset.topic !== state.topic) return false;
      const query = normalize(state.query);
      if (!query) return true;
      const source = normalize(item.dataset.searchText || item.textContent || "");
      return query.split(" ").every((word) => source.includes(word));
    }

    function renderTopics(restoreFocus = false) {
      if (!topicRail) return;
      topicRail.replaceChildren();
      activePath().topics.forEach((topic) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "topic-chip";
        button.textContent = topic;
        button.dataset.topic = topic;
        button.setAttribute("aria-pressed", String(state.topic === topic));
        button.addEventListener("click", () => {
          state.topic = topic;
          renderTopics(true);
          renderQuestions();
        });
        button.addEventListener("keydown", (event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          const chips = [...topicRail.querySelectorAll("button")];
          const current = chips.indexOf(button);
          const delta = event.key === "ArrowRight" ? 1 : -1;
          const next = chips[(current + delta + chips.length) % chips.length];
          next?.focus();
          next?.scrollIntoView({ block: "nearest", inline: "center" });
        });
        topicRail.append(button);
      });
      if (restoreFocus) {
        topicRail.querySelector(`[data-topic="${CSS.escape(state.topic)}"]`)?.focus({ preventScroll: true });
      }
    }

    function renderQuestions() {
      let visible = 0;
      items.forEach((item) => {
        const show = itemMatches(item);
        item.hidden = !show;
        if (!show) {
          item.open = false;
          return;
        }
        visible += 1;
        const index = item.querySelector("[data-question-index]");
        if (index) index.textContent = String(visible).padStart(2, "0");
      });
      if (emptyState) emptyState.hidden = visible !== 0;
      if (resultCount) resultCount.textContent = `${visible} ${visible === 1 ? "answer" : "answers"}`;
      if (clearSearch) clearSearch.hidden = state.query.length === 0;
      window.clearTimeout(announceTimer);
      announceTimer = window.setTimeout(() => {
        if (!liveStatus) return;
        const audience = state.audience === "kickstarter" ? "Kickstarter backer" : "direct pre-order";
        liveStatus.textContent = `Showing ${visible} ${audience} ${visible === 1 ? "answer" : "answers"}.`;
      }, 350);
    }

    function renderContext() {
      const content = activePath();
      root.dataset.audience = state.audience;
      if (audienceLabel) audienceLabel.textContent = content.label;
      if (audienceNote) audienceNote.textContent = content.note;
      if (contextTitle) contextTitle.textContent = content.contextTitle;
      if (contextCopy) contextCopy.textContent = content.contextCopy;
      if (resultsKicker) resultsKicker.textContent = content.resultsKicker;
      if (resultsHeading) resultsHeading.textContent = content.resultsHeading;
      if (search) search.placeholder = content.searchPlaceholder;
      if (emptySwitch) emptySwitch.textContent = content.emptySwitch;
      if (contextFacts) {
        contextFacts.replaceChildren(...content.facts.map(([term, definition]) => {
          const wrapper = document.createElement("div");
          const dt = document.createElement("dt");
          const dd = document.createElement("dd");
          dt.textContent = term;
          dd.textContent = definition;
          wrapper.append(dt, dd);
          return wrapper;
        }));
      }
      pathInputs.forEach((input) => {
        input.checked = input.value === state.audience;
      });
    }

    function updateUrl() {
      const url = new URL(window.location.href);
      url.searchParams.set("audience", state.audience);
      window.history.replaceState({ audience: state.audience }, "", url);
    }

    function selectAudience(audience, options = {}) {
      if (!PATHS[audience]) return;
      state.audience = audience;
      state.topic = "All";
      items.forEach((item) => { item.open = false; });
      renderContext();
      renderTopics();
      renderQuestions();
      updateUrl();
      if (options.focus) {
        pathInputs.find((candidate) => candidate.value === audience)?.focus({ preventScroll: true });
      }
      if (options.scroll) {
        pathSwitcher?.scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth", block: "center" });
      }
    }

    function resetSearch() {
      state.query = "";
      state.topic = "All";
      if (search) search.value = "";
      renderTopics();
      renderQuestions();
      search?.focus();
    }

    pathInputs.forEach((input) => {
      input.addEventListener("change", () => {
        if (input.checked) selectAudience(input.value);
      });
    });

    search?.addEventListener("input", () => {
      state.query = search.value.trim();
      if (state.query) state.topic = "All";
      renderTopics();
      renderQuestions();
    });

    clearSearch?.addEventListener("click", resetSearch);
    root.querySelector("[data-reset-search]")?.addEventListener("click", resetSearch);

    const togglePath = () => {
      const next = state.audience === "kickstarter" ? "direct" : "kickstarter";
      selectAudience(next, { focus: true, scroll: true });
    };
    root.querySelector("[data-switch-path]")?.addEventListener("click", togglePath);
    emptySwitch?.addEventListener("click", togglePath);

    root.querySelectorAll("[data-select-path]").forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        selectAudience(link.dataset.selectPath, { focus: true, scroll: true });
      });
    });

    faqList?.addEventListener("toggle", (event) => {
      const opened = event.target;
      if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
      items.forEach((item) => {
        if (item !== opened) item.open = false;
      });
    }, true);

    selectAudience(state.audience);
  }

  function initAll(scope = document) {
    if (scope.matches?.("[data-preorder-faq]")) init(scope);
    scope.querySelectorAll?.("[data-preorder-faq]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();


